import React, { useEffect } from 'react';
import { Box, Input } from '@podiumhq/design-system';

import {
  getGooglePlacesDetails,
  sendNewRelicError,
  splitGmbAddress,
} from 'lib/googleAutocomplete';

import { formatBusinessCountryCode } from '../BusinessDetails';
import { FormFieldWrapper } from '../FormElements';

import {
  GoogleAutocompleteNoResults,
  GoogleAutocompletePrediction,
  GoogleAutocompleteResultsList,
  GoogleAutocompleteWrapper,
  useGoogleAutocomplete,
  useKeyboardAutocompleteResults,
} from './';
import { useCheckoutContext } from 'contexts/CheckoutContextProvider';

export const GoogleAutocompleteAddress = ({
  addressFields = {},
  resetErrors,
  setAddressFields,
  ...inputProps
}) => {
  const {
    inputRef: inputAddressRef,
    listRef: addressListRef,
    getOnKeyDown: getOnAddressKeyDown,
    onKeyDown: onAddressKeyDown,
    setShouldHideResults: setShouldHideAddressResults,
    shouldHideResults: shouldHideAddressResults,
  } = useKeyboardAutocompleteResults();

  const formatAddressResults = (prediction) => ({
    description: prediction?.description,
    key: prediction?.place_id,
  });
  const displayResults = () => setShouldHideAddressResults(false);
  const hideResults = () => setShouldHideAddressResults(true);

  const { address: addressValue = '' } = addressFields;
  const { isMobile } = useCheckoutContext() || {};

  const {
    clearPredictions,
    getAutocompletePredictions,
    hasNoResultsRef,
    placePredictions,
  } = useGoogleAutocomplete(
    formatAddressResults,
    ['address'],
    isMobile ? 3 : 5
  );

  const selectGoogleAddress = async ({ description, key }) => {
    if (description?.length > 0) {
      try {
        const [address, city, state, country] = splitGmbAddress(description);
        const googlePlaceDetails = await getGooglePlacesDetails(key);

        setAddressFields &&
          setAddressFields({
            ...addressFields,
            address,
            city,
            country: formatBusinessCountryCode(country),
            state: state.toUpperCase(),
            zip: googlePlaceDetails?.zipCode,
          });

        resetErrors && resetErrors();
        setShouldHideAddressResults(true);
      } catch (error) {
        sendNewRelicError(error, 'getAddressPlacesDetails');
      }
    }
  };

  const handlePredictionSelection = (prediction) =>
    selectGoogleAddress(prediction).then(hideResults);

  useEffect(() => {
    if (addressValue?.length < 2) {
      clearPredictions();
    } else if (!shouldHideAddressResults) {
      getAutocompletePredictions(addressValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressValue, shouldHideAddressResults]);

  return (
    <Box position="relative">
      <FormFieldWrapper error={inputProps?.error}>
        <Input
          isInvalid={!!inputProps?.error}
          onFocus={displayResults}
          onKeyDown={onAddressKeyDown}
          ref={inputAddressRef}
          value={addressValue}
          variant="basic"
          {...inputProps}
        />
      </FormFieldWrapper>

      {addressValue?.length > 2 && !shouldHideAddressResults && (
        <GoogleAutocompleteWrapper hideResults={hideResults}>
          {placePredictions?.length > 0 && (
            <GoogleAutocompleteResultsList role="listbox" ref={addressListRef}>
              {placePredictions.map((prediction) => (
                <GoogleAutocompletePrediction
                  key={prediction.key}
                  description={prediction?.description}
                  handlePredictionSelection={handlePredictionSelection}
                  onKeyDown={getOnAddressKeyDown}
                  prediction={prediction}
                />
              ))}
            </GoogleAutocompleteResultsList>
          )}
          {hasNoResultsRef.current && <GoogleAutocompleteNoResults />}
        </GoogleAutocompleteWrapper>
      )}
    </Box>
  );
};
